import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FixedSizeList } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";
import { matchPath, useLocation } from "react-router-dom";

import { isMobile } from 'react-device-detect';

import * as SVG from 'svg.js';

import {
    List,
    ListItemButton,
    ListItemText,
    Box,
    ListItem,
    TextField,
    Button,
    Typography,
    IconButton,
    Grid,
    FormGroup,
    FormControlLabel,
    useTheme,
} from '@mui/material';
import { TabPanel, TabContext } from "@mui/lab";

import { OpenWith, VerticalAlignCenter } from "@mui/icons-material";

import apiClient from '../../../../services/api';

import store from '../../../../store';
import {
    setStoreData,
    setIsSceneUpdated,
    setSnapToRuler,
    setMoveElementsWithRuler,
    setUnsavedModifications,
    setIsWallDrawEnabled,
    setSelectedStoreElement,
    setCurrentTab,
    addFloorPlanElement,
    removeRulers,
    setIsInfoWindowVisible,
    /*
    addFloorPlanElement3D,
    addFloorPlanElement3DPosition
    */
} from '../../../../store/actions/Store';

import { createConfirmAction } from "../../../../store/actions/ConfirmDialog";

import { generateUniqueString, getScale } from "../../../../utils/Utils";

import TrashIcon from "../../../icons/TrashIcon";

import StyledSwitch from "../../../styled/Switch";
import StyledTab from "../../../styled/Tab";
import StyledTabs from "../../../styled/Tabs";

import ZonesTab from "./tabs/Zones";
import ReportTab from "./tabs/Report";
import PublicTab from "./tabs/Public";

import InfoWindow from './InfoWindow';

import { ELEMENTS_LAYER_ID, FLOOR_ELEMENT_ID } from "./layers/Elements";

const Projects = (props) => {
    const theme = useTheme();

    return(
        <Fragment>
            <TextField
                id="project-search"
                label={props.t('Search')}
                variant="outlined"
                size="small"
                value={props.searchProjects}
                onChange={props.filterProjects}
                fullWidth
                style={{ marginTop: '10px', marginBottom: '10px', color: theme.palette.storeDrawer.tabs.input.color }}
                sx={{
                    '& .MuiInputBase-input': {
                        color: theme.palette.storeDrawer.tabs.input.color
                    },
                    '& .MuiInputLabel-root': {
                        color: '#4C5E74'
                    },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: '#4C5E74',
                        },
                        '&:hover fieldset': {
                            borderColor: '#40B2E0',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#40B2E0',
                        }
                    }
                }}
            />
            { props.filteredProjects && props.filteredProjects.length > 0 ? (
                <AutoSizer style={{ width: '100%' }} disableWidth>
                    {({ height }) => (
                        <FixedSizeList height={height - 70} itemSize={72} itemCount={props.filteredProjects.length} overscanCount={8} style={{ overflow: 'auto', textOverflow: 'ellipsis' }}>
                            {props.renderProjectRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            ) : (
                <Typography style={{padding: '1em', display: (props.filteredProjects.length === 0 ? 'block' : 'none'), color: theme.palette.storeDrawer.tabs.label.color}}>
                    {props.t('No projects found!')}
                </Typography>
            )}
        </Fragment>
    )
}

const Products = (props) => {
    const theme = useTheme();

    return (
        <Fragment>
            <TextField
                id="product-search"
                label={props.t('Search')}
                variant="outlined"
                size='small'
                value={props.searchProducts}
                onChange={props.filterProducts}
                fullWidth
                autoFocus
                style={{ marginTop: '10px', marginBottom: '10px', color: theme.palette.storeDrawer.tabs.input.color }}
                sx={{
                    '& .MuiInputBase-input': {
                        color: theme.palette.storeDrawer.tabs.input.color
                    },
                    '& .MuiInputLabel-root': {
                        color: '#4C5E74'
                    },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: '#4C5E74',
                        },
                        '&:hover fieldset': {
                            borderColor: '#40B2E0',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#40B2E0',
                        }
                    }
                }}
            />
            { props.filteredProducts && props.filteredProducts.length > 0 ? (
                <AutoSizer style={{ width: '100%' }} disableWidth>
                    {({ height }) => (
                        <FixedSizeList height={height - 70} itemSize={72} itemCount={props.filteredProducts.length} overscanCount={8}>
                            {props.renderProductRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            ) : (
                <Typography style={{padding: '1em', display: (props.filteredProducts.length === 0 ? 'block' : 'none'), color: theme.palette.storeDrawer.tabs.label.color}}>
                    {props.t('No products found!')}
                </Typography>
            )}
        </Fragment>
    )
}

const Fixtures = (props) => {
    const theme = useTheme();

    return (
        <Fragment>
            <TextField
                id="fixtures-search"
                label={props.t('Search')}
                variant="outlined"
                size="small"
                value={props.searchFixtures}
                onChange={props.filterFixtures}
                fullWidth
                autoFocus
                style={{ marginTop: '10px', marginBottom: '10px', color: theme.palette.storeDrawer.tabs.input.color }}
                sx={{
                    '& .MuiInputBase-input': {
                        color: theme.palette.storeDrawer.tabs.input.color
                    },
                    '& .MuiInputLabel-root': {
                        color: '#4C5E74'
                    },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: '#4C5E74',
                        },
                        '&:hover fieldset': {
                            borderColor: '#40B2E0',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#40B2E0',
                        }
                    }
                }}
            />
            { props.filteredFixtures && props.filteredFixtures.length > 0 ? (
                <AutoSizer style={{ width: '100%' }} disableWidth>
                    {({ height }) => (
                        <FixedSizeList height={height - 70} itemSize={72} itemCount={props.filteredFixtures.length} overscanCount={9}>
                            {props.renderFixtureRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            ) : (
                <Typography style={{padding: '1em', display: (props.filteredFixtures.length === 0 ? 'block' : 'none'), color: theme.palette.storeDrawer.tabs.label.color}}>
                    {props.t('No fixtures found!')}
                </Typography>
            )}
        </Fragment>
    )
}

const Walls = (props) => {
    const theme = useTheme();

    return (
        <Box pt={1} height="100%" display="flex" flexDirection="column" flex="1" overflow="auto">
            <Button
                variant="contained"
                fullWidth
                style={{ color: '#FFF', backgroundColor: '#40B2E0', display: 'flex', flexDirection: 'column' }}
                onClick={() => {
                    store.dispatch(setIsWallDrawEnabled(true));
                    // store.dispatch(setIsSceneUpdated(true));
                }}
            >
                {props.t('Draw wall')}
            </Button>
            <div style={{ display: 'flex', flexDirection: 'column', flex: '1', overflowY: 'auto', height: '100%' }}>
                <List>
                    {props.walls && props.walls.length > 0 ? (
                        props.walls.map((item, index) => (
                            <ListItem sx={{ backgroundColor: index % 2 === 0 ? theme.palette.storeDrawer.tabs.list.backgroundColor : '', borderBottom: '1px solid ' + theme.palette.storeDrawer.tabs.list.borderBottom.color }} key={`wall`+index} style={{paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px'}} dense>
                                <ListItemText
                                    primary='Wall'
                                    secondary={'Size: '+Number(item.width).toFixed(2)+' x '+Number(item.depth).toFixed(2)}
                                    //primaryTypographyProps={{ style: { color: '#FFF', fontSize: '16px' }}} secondaryTypographyProps={{ style: { color: '#FFF' }}}
                                    primaryTypographyProps={{ style: { color: theme.palette.storeDrawer.tabs.list.color }}} secondaryTypographyProps={{ style: { color: theme.palette.storeDrawer.tabs.list.color }}}
                                />
                                {/*}
                                <IconButton
                                    onClick={() => {
                                        store.dispatch(createConfirmAction(props.handleDeleteWall, props.t('Are you sure?'), item))
                                    }}
                                >
                                    <TrashIcon />
                                </IconButton>
                                {*/}
                            </ListItem>
                        ))
                    ) : (
                        <Typography style={{padding: '1em', display: (props.walls.length === 0 ? 'block' : 'none'), color: theme.palette.storeDrawer.tabs.label.color}}>
                            {props.t('No walls added!')}
                        </Typography>
                    )}
                </List>
            </div>
        </Box>
    )
}

const Rulers = (props) => {
    const theme = useTheme();

    return (
        <Box sx={{width: '100%', padding: 1}}>
            {/*}
            <Grid container sx={{marginBottom: '10px'}}>
                <Grid item xs={6} sx={{paddingRight: '5px'}}>
                    <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        startIcon={<VerticalAlignCenter style={{transform: 'rotate(90deg)'}} />}
                        onClick={() => props.handleAddRuler('vertical')}
                        style={{ color: '#FFF', backgroundColor: '#40B2E0' }}
                    >
                        {props.t('Vertical')}
                    </Button>
                </Grid>
                <Grid item xs={6} sx={{paddingLeft: '5px'}}>
                    <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        color="black"
                        startIcon={<VerticalAlignCenter />}
                        onClick={() => props.handleAddRuler('horizontal')}
                        style={{ color: '#FFF', backgroundColor: '#40B2E0' }}
                    >
                        {props.t('Horizontal')}
                    </Button>
                </Grid>
            </Grid>
            {*/}
            <Fragment>
                <Typography style={{ color: theme.palette.storeDrawer.tabs.label.color, marginBottom: '10px' }}>{props.t('Help line Settings')}</Typography>
                <FormGroup>
                    <FormControlLabel
                        control={<StyledSwitch checked={props.snapToRulerEnabled} onChange={props.handleRulerSnapStateChange} />}
                        label={<div style={{ paddingLeft: '10px' }}>{props.t('Snap elements')}</div>} style={{ color: theme.palette.storeDrawer.tabs.label.color, marginLeft: '5px', marginRight: '0px', marginBottom: '10px' }}
                    />
                </FormGroup>
                <FormGroup>
                    <FormControlLabel
                        control={<StyledSwitch checked={props.moveElementsWithRulerEnabled} onChange={props.handleMoveElementsWithRuler} style={{ marginRight: '5px' }} />}
                        label={<div style={{ paddingLeft: '10px' }}>{props.t('Move snapped elements with help line')}</div>} style={{ color: theme.palette.storeDrawer.tabs.label.color, marginLeft: '5px', marginRight: '0px', marginBottom: '10px' }}
                    />
                </FormGroup>
            </Fragment>
            <Button
                size="small"
                variant='contained'
                fullWidth
                style={{ color: '#FFF', backgroundColor: '#40B2E0' }}
                onClick={() => store.dispatch(createConfirmAction(props.handleRemoveAllRulers, props.t('Are you sure?')))}
            >
                {props.t('Remove all')}
            </Button>
        </Box>
    );
}

export default function LeftSidePanel()
{
    const theme = useTheme();
    const location = useLocation();

    const storeData = useSelector((state) => state.store.storeData);
    const isSceneUpdated = useSelector((state) => state.store.isSceneUpdated);
    const isGridEnabled = useSelector((state) => state.store.isGridEnabled);
    const currentTab = useSelector((state) => state.store.currentTab);
    const selectedElement = useSelector((state) => state.store.selectedStoreElement);
    //const historyStep = useSelector((state) => state.store.historyStep);

    const [projects, setProjects] = useState([]);
    const [products, setProducts] = useState([]);
    const [fixtures, setFixtures] = useState([]);
    
    const [walls, setWalls] = useState([]);
    const [rulers, setRulers] = useState([]);

    const [searchProjects, setSearchProjects] = useState('');
    const [searchProducts, setSearchProducts] = useState('');
    const [searchFixtures, setSearchFixtures] = useState('');

    const [filteredProjects, setFilteredProjects] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    
    const [filteredFixtures, setFilteredFixtures] = useState([]);

    const [snapToRulerEnabled, setSnapToRulerEnabled] = useState(true);
    const [moveElementsWithRulerEnabled, setMoveElementsWithRulerEnabled] = useState(false);

    const [elements, setElements] = useState('projects');
    const [tools, setTools] = useState('walls');

    const st = storeData ? storeData.floor_plan : null;
    const stid = st ? st.id : null;

    const { t } = useTranslation();

    const is3D = matchPath({ path: '/app/easy-floor/map/' + st?.floor_plan_id + '/preview', end: true }, location.pathname);

    //const filter = createFilterOptions();

    useEffect(() => {
        apiClient.get('/api/projects?easyFloor=1').then(response => { setProjects(response.data.projects); setFilteredProjects(response.data.projects)});
        apiClient.get('/api/objects/products').then(response => { setProducts(response.data); setFilteredProducts(response.data) });
        apiClient.get('/api/objects/fixtures').then(response => { setFixtures(response.data); setFilteredFixtures(response.data) });
    }, []);

    useEffect(() => {
        if(storeData?.floor_plan_elements?.length > 0) {
            let wallsArray = []; 

            storeData?.floor_plan_elements?.forEach(item => {
                if(parseInt(item.is_wall) === 1) {
                    wallsArray.push(item);
                }
            });

            setWalls(wallsArray);
        }
    }, [storeData, isSceneUpdated]);

    const handleDeleteWall = (wall) => {
        if(!storeData || storeData.floor_plan_elements.length === 0 || !wall) {
            return;
        }

        const clone = storeData;
        const wallsArray = [];

        clone.floor_plan_elements.forEach((item, index) => {
            if(
                (wall.generated_id === undefined && item.id === wall.id) ||
                (wall.generated_id !== undefined && item.generated_id === wall.generated_id)
            ) {
                clone.floor_plan_elements.splice(index, 1);
            } else if(parseInt(item.is_wall) === 1) {
                wallsArray.push(item);
            }
        });

        setWalls(wallsArray);

        store.dispatch(setStoreData(clone));
        store.dispatch(setSelectedStoreElement(null));

        store.dispatch(setIsSceneUpdated(true));
    }

    function renderProductRow(props) {
        const { index, style } = props;
      
        return (
            <ListItem style={style} sx={{ backgroundColor: index % 2 === 0 ? theme.palette.storeDrawer.tabs.list.backgroundColor : '', borderBottom: '1px solid ' + theme.palette.storeDrawer.tabs.list.borderBottom.color }} key={`product`+index} component="div" disablePadding>
                <ListItemButton>
                    <ListItemText primary={filteredProducts[index].art_number} secondary={getSize(filteredProducts[index])} primaryTypographyProps={{ style: { color: theme.palette.storeDrawer.tabs.list.color }}} secondaryTypographyProps={{ style: { color: theme.palette.storeDrawer.tabs.list.color }}} />
                </ListItemButton>
            </ListItem>
        );
    }

    function renderFixtureRow(props) {
        const { index, style } = props;
      
        return (
            <ListItem style={style} sx={{ backgroundColor: index % 2 === 0 ? theme.palette.storeDrawer.tabs.list.backgroundColor : '', borderBottom: '1px solid ' + theme.palette.storeDrawer.tabs.list.borderBottom.color }} key={`fixture`+index} component="div" disablePadding>
                <ListItemButton>
                    <ListItemText primary={filteredFixtures[index].art_number} secondary={getSize(filteredFixtures[index])} primaryTypographyProps={{ style: { color: theme.palette.storeDrawer.tabs.list.color }}} secondaryTypographyProps={{ style: { color: theme.palette.storeDrawer.tabs.list.color }}} />
                </ListItemButton>
            </ListItem>
        );
    }

    function renderProjectRow(props) {
        const { index, style } = props;
      
        return (
            <ListItem
                style={style}
                key={`project`+index}
                component="div"
                disablePadding
                draggable={!isMobile ? true : false}
                onDragStart={event => {if (!isMobile) { onProjectDragStart(event, filteredProjects[index])}}}
                onTouchStart={event => {
                    if (event.target.id?.includes('drag_handle')) {
                        onProjectTouchStart(event, filteredProjects[index]);
                    }
                }}
                sx={{ backgroundColor: index % 2 === 0 ? theme.palette.storeDrawer.tabs.list.backgroundColor : '', borderBottom: '1px solid ' + theme.palette.storeDrawer.tabs.list.borderBottom.color }}
            >
                <ListItemButton>
                    <ListItemText primary={filteredProjects[index].name} secondary={getSize(filteredProjects[index])} primaryTypographyProps={{ style: { color: theme.palette.storeDrawer.tabs.list.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}} secondaryTypographyProps={{ style: { color: theme.palette.storeDrawer.tabs.list.color }}} />
                    {isMobile && (
                            <Button
                                id={'drag_handle'+index}
                                variant="contained"
                                size="small"
                                startIcon={<OpenWith />}
                            >
                                {t('Move')}
                            </Button>
                        )
                    }
                </ListItemButton>
            </ListItem>
        );
    }

    const onProjectDragStart = (event, project) => {
        event.preventDefault();

        if(is3D) {
            return;
        }

        const w = Number(project.width) !== 0 ? Number(project.width) : 100;
        const h = Number(project.depth) !== 0 ? Number(project.depth) : 100;
        const scale = getScale(ELEMENTS_LAYER_ID);
        const svgWrap = createBox({ x: event.pageX, y: event.pageY }, { w, h }, scale);

        document.body.appendChild(svgWrap);

        SVG(svgWrap.id)
            .viewbox(0, 0, w, h)
            .rect(w, h)
            .fill('#000')
            .style(Object.assign({stroke: 'rgba(0,0,0,0.33)', 'stroke-width': '0.25', cursor: 'none' }));

        document.onmousemove = e => {
            svgWrap.style.left = `${e.pageX - 1}px`;
            svgWrap.style.top = `${e.pageY - 1}px`;
        };
        
        svgWrap.onmouseup = e => {
            document.onmousemove = null;
            svgWrap.onmouseup = null;
        
            document.body.removeChild(svgWrap);
        
            const target = document.elementFromPoint(e.x, e.y)
                ? document.elementsFromPoint(e.x, e.y).find(obj => obj.nodeName === 'svg')
                : null;
        
            if(target) {
                const x = Number(target.getAttribute('x'));
                const y = Number(target.getAttribute('y'));
                const targetPosition = target.getBoundingClientRect();
                const position = {
                    x: Math.floor((e.x - targetPosition.left) / scale + x),
                    y: Math.floor((e.y - targetPosition.top) / scale + y)
                };

                if(isGridEnabled) {
                    position.x = Math.round(Number(position.x) / storeData.floor_plan.grid_width) * storeData.floor_plan.grid_width;
                    position.y = Math.round(Number(position.y) / storeData.floor_plan.grid_depth) * storeData.floor_plan.grid_depth;
                }

                projectDropped(project, position);
            }
        };

        document.onkeydown = e => {
            if(document.getElementById(svgWrap.id)) {
                document.body.removeChild(svgWrap);
            }
        }
    }

    const onProjectTouchStart = (event, project) => {
        const touch = event.touches[0];
        
        if(is3D) {
            return;
        }
    
        // Initialize drag operation
        const w = Number(project.width) !== 0 ? Number(project.width) : 100;
        const h = Number(project.depth) !== 0 ? Number(project.depth) : 100;
        const scale = getScale(ELEMENTS_LAYER_ID);
        const svgWrap = createBox({ x: touch.pageX, y: touch.pageY }, { w, h }, scale);
    
        document.body.appendChild(svgWrap);
    
        SVG(svgWrap.id)
            .viewbox(0, 0, w, h)
            .rect(w, h)
            .fill('#000')
            .style({
                stroke: 'rgba(0,0,0,0.33)',
                'stroke-width': '0.25',
                cursor: 'none'
            });
    
        const onTouchMove = e => {
            const touchMove = e.touches[0];
            svgWrap.style.left = `${touchMove.pageX - 1}px`;
            svgWrap.style.top = `${touchMove.pageY - 1}px`;
        };
    
        const onTouchEnd = e => {
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
    
            document.body.removeChild(svgWrap);
    
            const touchEnd = e.changedTouches[0];
            const target = document.elementFromPoint(touchEnd.pageX, touchEnd.pageY)
                ? document.elementsFromPoint(touchEnd.pageX, touchEnd.pageY).find(obj => obj.nodeName === 'svg')
                : null;
    
            if (target) {
                const x = Number(target.getAttribute('x'));
                const y = Number(target.getAttribute('y'));
                const targetPosition = target.getBoundingClientRect();
                const position = {
                    x: Math.floor((touchEnd.pageX - targetPosition.left) / scale + x),
                    y: Math.floor((touchEnd.pageY - targetPosition.top) / scale + y)
                };
    
                if (isGridEnabled) {
                    position.x = Math.round(Number(position.x) / storeData.floor_plan.grid_width) * storeData.floor_plan.grid_width;
                    position.y = Math.round(Number(position.y) / storeData.floor_plan.grid_depth) * storeData.floor_plan.grid_depth;
                }
    
                projectDropped(project, position);
            }
        };
    
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd, { passive: false });
    };

    const createBox = ({ x, y }, { w, h }, scale) => {
        const box = document.createElement('div');
        box.id = 'dragBox';
        box.style.lineHeight = '0';
        box.style.position = 'absolute';
        box.style.left = `${x - 1}px`;
        box.style.top = `${y - 1}px`;
        box.style.width = `${w}px`;
        box.style.height = `${h}px`;
        box.style.transformOrigin = '0 0';
        box.style.zIndex = '9999';
        box.style.transform = `scale(${scale})`;

        return box;
    }

    const getSize = (item) => {
        const sizeArray = [
            parseFloat(item.width).toFixed(2),
            parseFloat(item.height).toFixed(2),
            parseFloat(item.depth).toFixed(2)
        ];

        return 'Size: ' + sizeArray.join(' x ');
    }

    const filterProjects = (event) => {
        const keyword = event.target.value;
  
        if(keyword !== '') {
            const results = projects.filter(
                project => {
                    return project.name.toLowerCase().includes(keyword.toLowerCase())
                }
            );
  
            setFilteredProjects(results);
        } else {
            setFilteredProjects(projects)
        }
  
        setSearchProjects(keyword);
    }

    const filterProducts = (event) => {
        const keyword = event.target.value;
  
        if(keyword !== '') {
            const results = products.filter((product) => { return product.art_number.toLowerCase().includes(keyword.toLowerCase()) });
  
            setFilteredProducts(results);
        } else { setFilteredProducts(products) }
  
        setSearchProducts(keyword);
    }

    const filterFixtures = (event) => {
        const keyword = event.target.value;
  
        if(keyword !== '') {
            const results = fixtures.filter((fixture) => { return fixture.art_number.toLowerCase().includes(keyword.toLowerCase()) });
  
            setFilteredFixtures(results);
        } else { setFilteredFixtures(fixtures) }
  
        setSearchFixtures(keyword);
    }

    const projectDropped = (project, position) => {
        pushDroppedProject(project, position);
    }

    const pushDroppedProject = (project, position) => {
        let element = {
            id: 0,
            generated_id: generateUniqueString(),
            store_id: stid,
            project_id: project.id,
            name: project.name,
            number: project.number,
            width: project.width != 0 ? project.width : 100,
            height: project.height != 0 ? project.height : 100,
            depth: project.depth != 0 ? project.depth : 100,
            positionX: position.x,
            positionY: position.y,
            positionZ: 0,
            angle: 0,
            is_wall: 0,
            gltf: JSON.parse(project.has_model) ? 'data' : null
        }

        const index = storeData.floor_plan_elements_3D?.findIndex(
            el => el.project_id === project.id
        );

        if(index === -1) {
            apiClient.post('/api/projects/load-project-model', {id: project.id}).then(response => {
                if(!response.data.success) {
                    return;
                }

                element.model = response.data.model;

                if(response.data.models) {
                    element.models = response.data.models;
                }

                store.dispatch(addFloorPlanElement(element));
                store.dispatch(setUnsavedModifications(true));
                store.dispatch(setSelectedStoreElement(element));
            });

            return;
        }

        store.dispatch(addFloorPlanElement(element));
        store.dispatch(setUnsavedModifications(true));
        store.dispatch(setSelectedStoreElement(element));
    }

    const handleAddRuler = (type) => {
        const ruler = {
            elementType: 'ruler',
            generated_id: generateUniqueString(),
            id: null,
            type: type,
            x: 0,
            y: 0,
        };
        
        const clone = storeData;
        clone.rulers.push(ruler);

        setRulers(clone.rulers);
        store.dispatch(setStoreData(clone));

        store.dispatch(setIsSceneUpdated(true));
        store.dispatch(setUnsavedModifications(true));
    }

    const handleRemoveAllRulers = () => {
        store.dispatch(removeRulers());

        // if selected element ruler
        if (selectedElement !== FLOOR_ELEMENT_ID && selectedElement?.is_wall === undefined) {
            store.dispatch(setSelectedStoreElement(null));
            store.dispatch(setIsInfoWindowVisible(false));
        }

        store.dispatch(setIsSceneUpdated(true));
        store.dispatch(setUnsavedModifications(true));
    };

    const handleRulerSnapStateChange = (event) => {
        setSnapToRulerEnabled(event.target.checked);

        store.dispatch(setSnapToRuler(event.target.checked));

        store.dispatch(setIsSceneUpdated(true));
    }

    const handleMoveElementsWithRuler = (event) => {
        setMoveElementsWithRulerEnabled(event.target.checked);

        store.dispatch(setMoveElementsWithRuler(event.target.checked));

        store.dispatch(setIsSceneUpdated(true));
    }

    const handleZoneFocus = (zone) => {
        // TODO: zoom in to zone
    };

    return [
        <InfoWindow />,
        <TabContext value={currentTab || 'elements'}>
            <Box style={{backgroundColor: theme.palette.storeDrawer.tabs.backgroundColor, borderRadius: '8px', height: '40px' }}>
                <StyledTabs
                    variant="fullWidth"
                    value={currentTab || 'elements'}
                    onChange={(e, t) => store.dispatch(setCurrentTab(t))}
                >
                    <StyledTab label={t('Elements')} value="elements" />
                    <StyledTab label={t('Tools')} value="tools" />
                    <StyledTab label={t('Zones')} value="zones" />
                    <StyledTab label={t('Report')} value="report" />
                    <StyledTab label={t('Public')} value="public" />
                </StyledTabs>
            </Box>

            <TabPanel value="elements" sx={{ height: '100%', p: 0, pt: 1 }}>
                <TabContext value={elements}>
                    <StyledTabs
                        value={elements}
                        onChange={(e, t) => setElements(t)}
                        variant="fullWidth"
                    >
                        <StyledTab label={t('Projects')} value="projects" />
                        <StyledTab label={t('Fixtures')} value="fixtures" />
                        <StyledTab label={t('Products')} value="products" />
                    </StyledTabs>
                    <TabPanel value="projects" sx={{ height: 'calc(100% - 40px)', p: 0 }}>
                        <Projects
                            searchProjects={searchProjects}
                            filteredProjects={filteredProjects}
                            filterProjects={filterProjects}
                            renderProjectRow={renderProjectRow}
                            onProjectDragStart={onProjectDragStart}
                            t={t}
                        />
                    </TabPanel>
                    <TabPanel value="fixtures" sx={{ height: 'calc(100% - 40px)', p: 0 }}>
                        <Fixtures
                            searchFixtures={searchFixtures}
                            filteredFixtures={filteredFixtures}
                            filterFixtures={filterFixtures}
                            renderFixtureRow={renderFixtureRow}
                            t={t}
                        />
                    </TabPanel>
                    <TabPanel value="products" sx={{ height: 'calc(100% - 40px)', p: 0 }}>
                        <Products
                            searchProducts={searchProducts}
                            filteredProducts={filteredProducts}
                            filterProducts={filterProducts}
                            renderProductRow={renderProductRow}
                            t={t}
                        />
                    </TabPanel>
                </TabContext>
            </TabPanel>

            <TabPanel value="tools" sx={{ height: '100%', p: 0, pt: 1 }}>
                <TabContext value={tools}>
                    <StyledTabs
                        value={tools}
                        onChange={(e, t) => setTools(t)}
                        variant="fullWidth"
                    >
                        <StyledTab label={t('Walls')} value="walls" />
                        <StyledTab label={t('Help lines')} value="rulers" />
                    </StyledTabs>
                    <TabPanel value="walls" sx={{ height: 'calc(100% - 40px)', p: 0 }}>
                        <Walls
                            walls={walls}
                            handleDeleteWall={handleDeleteWall}
                            t={t}
                        />
                    </TabPanel>
                    <TabPanel value="rulers" sx={{ height: 'calc(100% - 40px)', p: 0 }}>
                        <Rulers
                            rulers={rulers}
                            snapToRulerEnabled={snapToRulerEnabled}
                            moveElementsWithRulerEnabled={moveElementsWithRulerEnabled}
                            handleAddRuler={handleAddRuler}
                            handleRemoveAllRulers={handleRemoveAllRulers}
                            handleRulerSnapStateChange={handleRulerSnapStateChange}
                            handleMoveElementsWithRuler={handleMoveElementsWithRuler}
                            t={t}
                        />
                    </TabPanel>
                </TabContext>
            </TabPanel>

            <TabPanel value="zones" sx={{ height: '100%', p: 0, pt: 1 }}>
                <ZonesTab onZoneFocusClick={handleZoneFocus} />
            </TabPanel>

            <TabPanel value="report" sx={{ height: '100%', p: 0, pt: 1 }}>
                <ReportTab />
            </TabPanel>

            <TabPanel value="public" sx={{ height: '100%', p: 0, pt: 1 }}>
                <PublicTab />
            </TabPanel>
        </TabContext>
    ];
}
